import { createTheme } from '@mui/material/styles';
import { colors, typography, spacing, shadows, breakpoints } from './design-system';

export const createAppTheme = (mode = 'light') => createTheme({
  palette: {
    mode,
    primary: {
      main: colors.primary[500],      // Verde brillante (#4caf50)
      dark: colors.primary[900],      // Verde oscuro (#1b5e20)
      light: colors.primary[100],     // Verde claro (#c8e6c9)
      contrastText: colors.neutral.white,
    },
    secondary: {
      main: colors.secondary[500],    // Amarillo dorado (#ffd54f)
      dark: colors.secondary[700],    // Amarillo oscuro (#ffb300)
      light: colors.secondary[100],   // Amarillo claro (#fff9c4)
      contrastText: colors.text.primary,
    },
    background: {
      default: mode === 'dark' ? '#0b120d' : colors.background.default,
      paper: mode === 'dark' ? '#17231a' : colors.background.paper,
    },
    text: {
      primary: mode === 'dark' ? '#f5f8f3' : colors.text.primary,
      secondary: mode === 'dark' ? '#b9c6ba' : colors.text.secondary,
      disabled: mode === 'dark' ? '#7f8e81' : colors.text.disabled,
    },
    success: {
      main: colors.success.main,
    },
    warning: {
      main: colors.warning.main,
    },
    error: {
      main: colors.error.main,
    },
    info: {
      main: colors.info.main,
    },
    divider: mode === 'dark' ? '#304536' : colors.divider,
  },
  typography: {
    fontFamily: typography.fontFamily.primary,
    fontSize: 16,
    fontWeightLight: typography.fontWeight.light,
    fontWeightRegular: typography.fontWeight.regular,
    fontWeightMedium: typography.fontWeight.medium,
    fontWeightBold: typography.fontWeight.bold,
    h1: typography.styles.h1,
    h2: typography.styles.h2,
    h3: typography.styles.h3,
    h4: typography.styles.h4,
    h5: typography.styles.h5,
    h6: typography.styles.h6,
    body1: typography.styles.body,
    body2: typography.styles.bodySmall,
    button: typography.styles.button,
    caption: typography.styles.caption,
    overline: typography.styles.overline,
  },
  spacing: spacing.base, // MUI usa un valor base de 8px por defecto, nosotros 4px
  breakpoints: {
    values: breakpoints.values,
  },
  shadows: [
    shadows.none,
    shadows.elevation[1],
    shadows.elevation[2],
    shadows.elevation[3],
    shadows.elevation[4],
    shadows.elevation[4],
    shadows.elevation[6],
    shadows.elevation[6],
    shadows.elevation[8],
    shadows.elevation[8],
    shadows.elevation[8],
    shadows.elevation[8],
    shadows.elevation[12],
    shadows.elevation[12],
    shadows.elevation[12],
    shadows.elevation[12],
    shadows.elevation[16],
    shadows.elevation[16],
    shadows.elevation[16],
    shadows.elevation[16],
    shadows.elevation[16],
    shadows.elevation[16],
    shadows.elevation[16],
    shadows.elevation[16],
    shadows.elevation[24],
  ],
  shape: {
    borderRadius: spacing.borderRadius.md, // 8px
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          backgroundColor: mode === 'dark' ? '#0b120d' : colors.background.default,
        },
        body: {
          backgroundColor: mode === 'dark' ? '#0b120d' : colors.background.default,
        },
        '#root': {
          minHeight: '100vh',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: spacing.borderRadius.md,
          textTransform: 'none', // No uppercase por defecto
          fontWeight: typography.fontWeight.semibold,
          padding: `${spacing[2]}px ${spacing[4]}px`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: spacing.borderRadius.lg,
          boxShadow: shadows.card,
          ...(mode === 'dark' && {
            backgroundImage: 'none',
            border: '1px solid #304536',
          }),
          '&:hover': {
            boxShadow: shadows.cardHover,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: spacing.borderRadius.md,
          ...(mode === 'dark' && {
            backgroundImage: 'none',
          }),
        },
      },
    },
    // Form controls occur on most routed pages. Define their surfaces once so
    // native MUI fields remain legible without per-page dark-mode exceptions.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? '#f5f8f3' : colors.text.primary,
          backgroundColor: mode === 'dark' ? '#111c14' : colors.background.paper,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === 'dark' ? '#526657' : colors.neutral[300],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === 'dark' ? '#8faf93' : colors.neutral[500],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary[500],
          },
          '&.Mui-disabled': {
            backgroundColor: mode === 'dark' ? '#101812' : colors.neutral[100],
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? '#b9c6ba' : colors.text.secondary,
          '&.Mui-focused': { color: colors.primary[500] },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: mode === 'dark' ? '#b9c6ba' : colors.text.secondary,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          border: mode === 'dark' ? '1px solid #304536' : undefined,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          ...(mode === 'dark' && { border: '1px solid #304536' }),
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: mode === 'dark' ? '#304536' : colors.divider,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'dark' ? '#111c14' : colors.neutral[50],
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: mode === 'dark' ? '#1d2d20' : colors.neutral[50],
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? '#b9c6ba' : colors.text.secondary,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? '#b9c6ba' : colors.text.secondary,
          borderColor: mode === 'dark' ? '#526657' : colors.neutral[300],
          '&.Mui-selected': {
            color: mode === 'dark' ? '#d7f5d9' : colors.primary[800],
            backgroundColor: mode === 'dark' ? '#214d29' : colors.primary[50],
          },
        },
      },
    },
  },
});

const theme = createAppTheme();

export default theme;
